import React, { forwardRef, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import { cssCustomPropInternalMegaMenuPrefix, cwRTLTranslate, fadeTransitions, flyoutPanelTransition, linkHoverTransition, MegaMenuCSSCustomProperties, primaryNavigationId, } from './MegaMenu.config';
import IconButton, { IconAnchorButton, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { useMegaMenu } from './MegaMenuContext';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { css } from '@emotion/react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { MegaMenuScrollHinter } from './MegaMenuScrollHinter';
import { createCSSPropertyTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { useFocusInitial, useFocusRestore, useFocusTrap, } from '@polestar/component-warehouse-react/hooks/focus';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { StaticCloseButton } from './MegaMenuSharedCloseButton';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
import useTrackingData, { formatTrackingLabel, } from '@polestar/component-warehouse-react/hooks/useTrackingData';
import { DataLayerAction, DataLayerCategory, DataLayerEvent, } from '@polestar/component-warehouse-react/tokens/analytics/Analytics';
import { setCSSCustomProp } from '@polestar/component-warehouse-react/hooks/useCSSCustomProp';
import { MarketSelectorCSSCustomProps } from '@polestar/component-warehouse-react/components/market-selector/MarketSelector';
import { MegaMenuMultiPurposeLink } from '@polestar/component-warehouse-react/components/mega-menu/internal/MegaMenuMultiPurposeLink';
var MainNavigation;
(function (MainNavigation) {
    MainNavigation["mobileTranslate"] = "--cw-internal-mega-menu-mobile-translate";
})(MainNavigation || (MainNavigation = {}));
/**
 * Contains the main navigation items.
 */
export const MegaMenuPrimaryNavigation = forwardRef(({ navigation, actions, megaMenuId, ariaLabels, ...props }, ref) => {
    const { menuActive, flyoutActive, setFlyoutActive, setActiveFlyoutIndex } = useMegaMenu();
    const listRef = useRef(undefined);
    // only support focus trap on mobile/tablet keyboard mode
    const { isKeyboardControlled } = useInteractionMode();
    const { name } = useMediaQuery();
    const nonDesktopDevice = name === DeviceTypes.mobile || name === DeviceTypes.tablet;
    const supportFocusTrap = isKeyboardControlled && nonDesktopDevice;
    const tabIndex = nonDesktopDevice ? (menuActive && !flyoutActive ? 0 : -1) : 0;
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(ref, internalRef);
    useFocusTrap(internalRef, supportFocusTrap && menuActive && !flyoutActive);
    useFocusRestore(supportFocusTrap);
    const onActionMouseEnter = () => {
        setFlyoutActive(false);
        setActiveFlyoutIndex(null);
    };
    const handleFocus = () => {
        setFlyoutActive(false);
        setActiveFlyoutIndex(null);
    };
    // tracking
    const { pushDataLayer } = useTrackingData();
    const handleActionClick = (e, label, 
    // TODO: works the same for touch?
    onClick) => {
        pushDataLayer({
            event: DataLayerEvent.LINK_CLICK,
            category: DataLayerCategory.MENU,
            action: DataLayerAction.LINK_CLICK,
            label: formatTrackingLabel(label),
        });
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    useFocusInitial(internalRef, menuActive &&
        isKeyboardControlled &&
        (name === DeviceTypes.mobile || name === DeviceTypes.tablet));
    return (React.createElement(PrimaryNavigation, { ...props, ref: combinedRef, style: {
            [MainNavigation.mobileTranslate]: menuActive ? '0%' : '100%',
        }, id: `${megaMenuId}-${primaryNavigationId}`, "data-type": "mega-menu-navigation" },
        React.createElement(MegaMenuScrollHinter, { isSecondary: false },
            React.createElement(NavigationList, { role: "list", ref: listRef }, navigation.map(({ name, href, external, chevronLabel, isCar, eventLabel, panelIndex, ...navProps }, index) => {
                const linkData = {
                    label: name,
                    href,
                    external,
                    ariaLabel: navProps['aria-label'],
                    chevronLabel,
                    eventLabel,
                };
                return (React.createElement(ListItem, { key: index, "data-group": isCar ? 'main' : 'secondary' },
                    React.createElement(MegaMenuMultiPurposeLink, { link: linkData, navigationIndex: panelIndex, megaMenuId: megaMenuId, openExternalLinkText: ariaLabels.openExternalLink, ...navProps })));
            }))),
        actions && (React.createElement(ActionsList, null, actions.map((action) => {
            const untilDesktop = name === DeviceTypes.mobile || name === DeviceTypes.tablet;
            const { icon, eventLabel, onClick, ...item } = action;
            const wrapperOnClick = (e) => {
                if (icon === 'globe') {
                    // check if MarketSelector then update the CSS Custom Property to show it needs to open or not
                    setCSSCustomProp(MarketSelectorCSSCustomProps.open, '1');
                    // TODO: adjust internal state of MegaMenu
                }
                handleActionClick(e, eventLabel, onClick);
            };
            let actionProps;
            if (
            // @ts-ignore
            (action === null || action === void 0 ? void 0 : action.href) !== undefined) {
                actionProps = {
                    'data-type': `icon-${icon}`,
                    ...item,
                    icon: Icons[icon],
                    href: action.href,
                    ...aria({
                        label: item === null || item === void 0 ? void 0 : item['aria-label'],
                    }),
                    pairedWithText: !untilDesktop,
                    tabIndex: tabIndex,
                    size: untilDesktop ? Sizes.large : Sizes.medium,
                    onMouseEnter: onActionMouseEnter,
                    onFocus: handleFocus,
                    onClick: wrapperOnClick,
                };
            }
            else {
                // only for the global site to show a MarketSelector
                actionProps = {
                    'data-type': `icon-${icon}`,
                    ...item,
                    icon: Icons[icon],
                    ...aria({
                        label: item === null || item === void 0 ? void 0 : item['aria-label'],
                    }),
                    pairedWithText: !untilDesktop,
                    tabIndex: tabIndex,
                    size: untilDesktop ? Sizes.large : Sizes.medium,
                    onFocus: handleFocus,
                    onMouseEnter: onActionMouseEnter,
                    onClick: wrapperOnClick,
                };
            }
            return (React.createElement("li", { key: action.icon },
                (actionProps === null || actionProps === void 0 ? void 0 : actionProps.href) !== undefined && React.createElement(StyledIconAnchorButton, { ...actionProps }),
                (actionProps === null || actionProps === void 0 ? void 0 : actionProps.href) === undefined && React.createElement(StyledIconButton, { ...actionProps })));
        }))),
        nonDesktopDevice && (React.createElement(StaticCloseButton, { "aria-label": ariaLabels.closeMenu, hideOnPointer: true, tabIndex: tabIndex }))));
});
var MegaMenuPrimaryNavigationCSSCustomProp;
(function (MegaMenuPrimaryNavigationCSSCustomProp) {
    MegaMenuPrimaryNavigationCSSCustomProp["opacity"] = "--primary-navigation-opacity";
})(MegaMenuPrimaryNavigationCSSCustomProp || (MegaMenuPrimaryNavigationCSSCustomProp = {}));
const primaryNavigationNavigationLinkStates = css `
  ${MegaMenuPrimaryNavigationCSSCustomProp.opacity}: 1;

  &:has(
      [data-type='primary-navigation-item']:hover,
      [data-type*='icon-']:hover,
      [data-type='primary-navigation-item']:focus-within,
      [data-type*='icon-']:focus-within,
      [data-type='primary-navigation-item'] [aria-expanded='true']
    ) {
    ${MegaMenuPrimaryNavigationCSSCustomProp.opacity}: ${Opacities.medium};
  }

  [data-type='primary-navigation-item'],
  [data-type*='icon-'] {
    opacity: var(${MegaMenuPrimaryNavigationCSSCustomProp.opacity});
  }

  [data-type='primary-navigation-item'],
  [data-type*='icon-'] {
    &:has([aria-expanded='true']),
    &:hover,
    &:focus-within {
      opacity: ${Opacities.full};
    }
  }
`;
/**
 * Ensure that initially there is no transition to prevent issues with RTL/LTR that the wrappers move from one side of the viewport to the other side.
 */
const PrimaryNavigation = forwardRef((props, ref) => {
    const [transitionDuration, setTransitionDuration] = useState('0ms');
    useEffect(() => {
        setTransitionDuration(flyoutPanelTransition);
    }, []);
    return React.createElement(PrimaryNavigationContent, { ref: ref, ...props, transitionDuration: transitionDuration });
});
const PrimaryNavigationContent = styled.nav(({ theme, transitionDuration }) => css `
    display: flex;
    flex-direction: column;
    justify-content: end;
    inline-size: 100%;
    color: ${theme.megaMenu.color};
    pointer-events: auto;

    ${MediaQueries.untilDesktop} {
      background: var(${MegaMenuCSSCustomProperties.background});
      position: fixed;
      inset: 0;
      overflow: hidden;
      translate: calc(var(${MainNavigation.mobileTranslate}, 0) * var(${cwRTLTranslate}, 1)) 0;
      transition: ${transitionDuration};
      padding: ${createCustomSpacing(56 / 16)} 6vw 0;
    }

    ${MediaQueries.tablet} {
      inline-size: min(100%, var(${MegaMenuCSSCustomProperties.tabletMaxInlineSize}));
      inset-inline: auto 0;
      inset-block: 0;
      padding: ${Spacings.xxxLarge} ${Spacings.xxLarge} 0;
    }

    ${MediaQueries.desktop} {
      inline-size: 100%;
      flex-direction: row;
      translate: none;
      transition: none;
      padding: unset;
      gap: inherit;
    }

    ${primaryNavigationNavigationLinkStates};
  `);
const NavigationList = styled.ul `
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  ${MediaQueries.desktop} {
    flex-direction: row;
    align-items: center;
    flex-shrink: 1;
    gap: ${Spacings.large};
    inline-size: auto;
    align-self: end;
  }
`;
const ListItem = styled.li `
  position: relative;
  max-inline-size: 100%;

  ${MediaQueries.untilDesktop} {
    &[data-group='main'] + &[data-group='secondary'] {
      margin-block-start: ${Spacings.large};
    }
  }
`;
const ActionsList = styled.ul `
  display: flex;
  flex-direction: row;

  ${MediaQueries.untilDesktop} {
    padding: ${Spacings.small} 0;
  }

  button,
  a {
    color: currentColor;
    transition: ${createCSSPropertyTransition('color', fadeTransitions)}, ${linkHoverTransition};
  }

  li:first-of-type {
    margin-inline-start: -${Spacings.xSmall};
  }
  li:last-of-type {
    margin-inline-end: -${Spacings.xSmall};
  }

  ${MediaQueries.untilDesktop} {
    li:first-of-type {
      margin-inline-start: -${Spacings.small};
    }
    li:last-of-type {
      margin-inline-end: -${Spacings.small};
    }
  }
`;
/*
 * The span of `IconAnchorButton` had a transition that was interfering with theme switch on the links
 */
const StyledIconAnchorButton = styled(IconAnchorButton) `
  &:before {
    /* prevent 48px width due to different alignment inside the MegaMenu */
    min-width: ${Spacings.xLarge};
  }

  span {
    transition: none;
  }
`;
/*
 * The span of `IconButton` had a transition that was interfering with theme switch on the links
 */
const StyledIconButton = styled(IconButton) `
  &:before {
    /* prevent 48px width due to different alignment inside the MegaMenu */
    min-width: ${Spacings.xLarge};
  }

  span {
    transition: none;
  }
`;
